import { createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AreaPermissions, AppArea, PermissionLevel } from "@shared/schema";
import { Eye, Lock } from "lucide-react";

interface PermissionsContextValue {
  isOwner: boolean;
  permissions: AreaPermissions | null;
  isLoading: boolean;
  can: (area: AppArea, level?: PermissionLevel) => boolean;
  canEdit: (area: AppArea) => boolean;
  canView: (area: AppArea) => boolean;
  isDisabled: (area: AppArea) => boolean;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  isOwner: false,
  permissions: null,
  isLoading: true,
  can: () => true,
  canEdit: () => true,
  canView: () => true,
  isDisabled: () => false,
});

export function PermissionsProvider({ children, enabled }: { children: React.ReactNode; enabled: boolean }) {
  const permsQuery = useQuery<{ isOwner: boolean; merged: AreaPermissions }>({
    queryKey: ["/api/app/my-permissions"],
    enabled,
  });

  const perms = permsQuery.data?.merged ?? null;
  const isOwner = permsQuery.data?.isOwner ?? false;

  const can = (area: AppArea, level: PermissionLevel = "view") => {
    if (isOwner) return true;
    if (!perms) return true;
    const current = perms[area];
    if (level === "edit") return current === "edit";
    if (level === "view") return current === "view" || current === "edit";
    return current !== "disabled";
  };

  const value: PermissionsContextValue = {
    isOwner,
    permissions: perms,
    isLoading: permsQuery.isLoading,
    can,
    canEdit: (area) => can(area, "edit"),
    canView: (area) => can(area, "view"),
    isDisabled: (area) => !can(area, "view"),
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}

export function ViewOnlyBanner({ area }: { area: AppArea }) {
  const { canEdit, isDisabled } = usePermissions();

  if (isDisabled(area)) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 mb-4" data-testid="banner-access-disabled">
        <Lock className="h-4 w-4 text-red-500 shrink-0" />
        <span className="text-sm text-red-700">You do not have access to this section. Contact your team owner to update your permissions.</span>
      </div>
    );
  }

  if (!canEdit(area)) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 mb-4" data-testid="banner-view-only">
        <Eye className="h-4 w-4 text-amber-600 shrink-0" />
        <span className="text-sm text-amber-700">You have view-only access to this section. Editing is restricted.</span>
      </div>
    );
  }

  return null;
}
